/**
 * Three-way load-state derivation for a React Query result, for surfaces that must not render an
 * empty state when the read actually failed (franklycatering #674, #755).
 *
 * `isLoading` is NOT the right predicate. React Query derives it as `isPending && isFetching`
 * (query-core 5.102.8, queryObserver.js:240), so a query that cannot reach the network —
 * networkMode "online", fetchStatus "paused", which is what wifi off / airplane mode / DevTools
 * Offline produces — reports isLoading false, isError false and undefined data, and falls straight
 * through to the empty branch. That is the bug, unfixed, on the most common "can't load" path
 * there is.
 *
 * `isPending && isPaused`, not bare `isPaused`: a paused *refetch* that already holds data is
 * status "success" + paused, and bare `isPaused` would stamp an offline notice over a screen full
 * of perfectly good rows.
 *
 * `canRetry` is `!isPaused`, not `!offline`. refetch() early-returns into continueRetry() whenever
 * fetchStatus is not "idle" (query-core query.js:156-162; continueRetry is
 * `() => { isRetryCancelled = false }`, retryer.js:48-50) — it dispatches nothing. A query that
 * errored while online and then had a refetch pause is status "error" + paused: isPending is
 * false, so `offline` is false, so a button gated on `!offline` would render and provably do
 * nothing.
 *
 * `canRetry` is necessary, not sufficient. "Not idle" covers fetchStatus "fetching" too, so a
 * refetch already in flight is equally inert while `canRetry` is true. Gate a retry button's
 * PRESENCE on `canRetry` and its DISABLED state on `isFetching` — neither alone closes the hole.
 */

export interface LoadStateInput {
  isPending: boolean;
  isError: boolean;
  isPaused: boolean;
}

export interface LoadState {
  /** First fetch in flight and reachable — show a skeleton. */
  loading: boolean;
  /** The read failed, or cannot even be attempted — show a notice, never an empty state. */
  failed: boolean;
  /** Failed specifically because the browser is offline — different copy. */
  offline: boolean;
  /** refetch() is inert while paused. Necessary but not sufficient — pair with `isFetching`. */
  canRetry: boolean;
}

export function deriveLoadState({ isPending, isError, isPaused }: LoadStateInput): LoadState {
  const offline = isPending && isPaused;
  const failed = isError || offline;
  return { loading: isPending && !failed, failed, offline, canRetry: !isPaused };
}
